import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api, fileUrl } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useI18n } from '@/i18n';
import { Button } from '@/components/ui/button';
import { ChevronLeft, Crown, Users, Share2, Trash2, LogOut, MessageCircle, Wifi, UserX } from 'lucide-react';
import { toast } from 'sonner';
import AddFriendButton from '@/components/AddFriendButton';
import ChatComposer from '@/components/ChatComposer';
import UserLink from '@/components/UserLink';
import ConvoyMemberMenu from '@/components/ConvoyMemberMenu';
import ImageLightbox from '@/components/ImageLightbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export default function ConvoyDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [comments, setComments] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get(`/convoys/${id}`),
        api.get(`/convoys/${id}/comments`),
      ]);
      setC(a.data);
      setComments(b.data);
    } catch { toast.error(t('convoys.not_found')); }
  };

  useEffect(() => {
    load();
    const i = setInterval(load, 10000);
    return () => clearInterval(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Heartbeat
  useEffect(() => {
    if (!user || user === false) return;
    api.post('/presence/heartbeat').catch(() => {});
    const h = setInterval(() => api.post('/presence/heartbeat').catch(() => {}), 30000);
    return () => clearInterval(h);
  }, [user]);

  // Scroll to chat section if URL hash is #chat
  useEffect(() => {
    if (!c) return;
    if (typeof window !== 'undefined' && window.location.hash === '#chat') {
      requestAnimationFrame(() => {
        const el = document.querySelector('[data-testid="convoy-chat-list"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [c]);

  if (!c) return <div className="min-h-screen grid place-items-center text-white/60">{t('common.loading')}</div>;

  const isMember = user && user !== false && c.members.some((m) => m.user_id === user.id);
  const isHost = user && user !== false && c.host_id === user.id;
  const isAdmin = user && user !== false && user.role === 'admin';
  const isFull = c.members_count >= c.max_size;
  const hasPendingRequest = user && user !== false && (c.join_requests || []).some((r) => r.user_id === user.id);
  const pendingRequests = c.join_requests || [];

  const join = async () => {
    if (!user || user === false) { toast.message(t('auth.must_login')); return; }
    try {
      const { data } = await api.post(`/convoys/${id}/join`);
      setC(data);
      if (data.pending_join) {
        toast.success(t('convoys.request_sent') || 'Demande envoyée à l\'hôte');
      } else {
        toast.success(t('convoys.joined_toast'));
      }
    }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const cancelRequest = async () => {
    try {
      await api.delete(`/convoys/${id}/join`);
      // Optimistic local update
      setC({ ...c, join_requests: (c.join_requests || []).filter((r) => r.user_id !== user.id) });
      toast.success(t('convoys.request_cancelled') || 'Demande annulée');
    } catch { toast.error('Erreur'); }
  };
  const acceptRequest = async (uid) => {
    try {
      const { data } = await api.post(`/convoys/${id}/requests/${uid}/accept`);
      setC(data);
      toast.success(t('convoys.member_accepted') || 'Joueur accepté');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const rejectRequest = async (uid) => {
    try {
      await api.post(`/convoys/${id}/requests/${uid}/reject`);
      setC({ ...c, join_requests: (c.join_requests || []).filter((r) => r.user_id !== uid) });
      toast.success(t('convoys.member_rejected') || 'Demande refusée');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const leave = async () => {
    try { const { data } = await api.post(`/convoys/${id}/leave`); setC(data); toast.success(t('convoys.left_toast')); }
    catch { toast.error('Erreur'); }
  };
  const del = async () => {
    setDeleting(true);
    try {
      await api.delete(`/convoys/${id}`);
      toast.success(t('convoys.deleted'));
      setConfirmDel(false);
      navigate('/convoys');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur de suppression');
    } finally {
      setDeleting(false);
    }
  };
  const share = async () => {
    const url = `${window.location.origin}/convoys/${id}`;
    try { await navigator.clipboard.writeText(url); toast.success(t('convoys.copied')); }
    catch { window.prompt('Lien du convoi', url); }
  };
  const submit = async ({ text, image_path }) => {
    if (!user || user === false) { toast.message(t('auth.must_login')); return; }
    try {
      const { data } = await api.post(`/convoys/${id}/comments`, { text, image_path });
      setComments([...comments, data]);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  return (
    <div className="min-h-screen pt-24 pb-24 px-6 bg-[#0A0A0A] text-white">
      <div className="max-w-4xl mx-auto">
        <Link to="/convoys" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/50 hover:text-white">
          <ChevronLeft className="w-3 h-3" /> {t('nav.convoys')}
        </Link>

        {/* Hero */}
        <div className="mt-4 glass rounded-3xl p-6 md:p-8 relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full blur-3xl opacity-30 bg-[#FF007F]" />
          <div className="relative">
            <h1 className="font-display text-4xl lg:text-6xl font-black uppercase tracking-tighter">{c.name}</h1>
            {c.description && <p className="text-white/70 mt-3 max-w-2xl">{c.description}</p>}
            <div className="mt-4 flex items-center gap-4 text-sm text-white/60 flex-wrap">
              <div className="inline-flex items-center gap-1.5"><Crown className="w-4 h-4 text-[#FFD479]" /> {c.host_name}</div>
              <div className="inline-flex items-center gap-1.5"><Users className="w-4 h-4" /> {c.members_count} / {c.max_size} {t('convoys.members')}</div>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {!isMember && !hasPendingRequest && (
                <Button
                  data-testid="convoy-detail-join"
                  onClick={join}
                  disabled={isFull}
                  className="rounded-full bg-[#FF007F] hover:bg-[#FF007F]/90 text-white font-bold uppercase tracking-wide"
                >
                  {isFull ? t('convoys.full') : (t('convoys.request_join') || t('convoys.join'))}
                </Button>
              )}
              {!isMember && hasPendingRequest && (
                <Button
                  data-testid="convoy-detail-cancel-request"
                  onClick={cancelRequest}
                  variant="ghost"
                  className="rounded-full bg-[#FFD479]/10 border border-[#FFD479]/40 text-[#FFD479] hover:bg-[#FFD479]/20"
                >
                  ⏳ {t('convoys.cancel_request') || 'Annuler ma demande'}
                </Button>
              )}
              {isMember && !isHost && (
                <Button
                  data-testid="convoy-detail-leave"
                  onClick={leave}
                  variant="ghost"
                  className="rounded-full border border-white/10 hover:bg-white/5"
                >
                  <LogOut className="w-4 h-4 mr-2" /> {t('convoys.leave')}
                </Button>
              )}
              <Button
                data-testid="convoy-share-btn"
                onClick={share}
                variant="ghost"
                className="rounded-full border border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/10"
              >
                <Share2 className="w-4 h-4 mr-2" /> {t('convoys.share')}
              </Button>
              {(isHost || isAdmin) && (
                <Button
                  data-testid="convoy-detail-delete"
                  onClick={() => setConfirmDel(true)}
                  variant="ghost"
                  className="rounded-full text-red-300 hover:bg-red-500/10 border border-red-500/20"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> {t('convoys.delete')}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Members */}
        <div className="mt-6 glass rounded-2xl p-5">
          <div className="text-xs uppercase tracking-[0.2em] text-white/40 font-bold mb-3 inline-flex items-center gap-2">
            <Wifi className="w-3 h-3 text-emerald-400 animate-pulse" />
            {t('convoys.crew')}
          </div>
          <div className="flex flex-wrap gap-2">
            {c.members.map((m) => (
              <div key={m.user_id} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
                {m.avatar_url ? (
                  <img src={m.avatar_url} alt={m.name} className="w-6 h-6 rounded-full" />
                ) : (
                  <div className="w-6 h-6 rounded-full bg-[#00F0FF] text-black grid place-items-center text-[10px] font-bold">
                    {m.name[0]?.toUpperCase()}
                  </div>
                )}
                <span className="text-sm"><UserLink userId={m.user_id} name={m.name} /></span>
                {m.user_id === c.host_id && <Crown className="w-3 h-3 text-[#FFD479]" />}
                <AddFriendButton userId={m.user_id} userName={m.name} variant="icon" size="xs" />
                {isHost && m.user_id !== c.host_id && (
                  <ConvoyMemberMenu
                    convoyId={c.id}
                    memberId={m.user_id}
                    memberName={m.name}
                    onUpdated={setC}
                  />
                )}
              </div>
            ))}
          </div>
          {isHost && (c.banned || []).length > 0 && (
            <div className="mt-4 pt-4 border-t border-white/10" data-testid="convoy-banned-section">
              <div className="text-[10px] uppercase tracking-[0.2em] text-red-300/70 font-bold mb-2">
                {t('convoys.host.banned')} ({c.banned.length})
              </div>
              <div className="flex flex-wrap gap-2">
                {c.banned.map((uid) => (
                  <div key={uid} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/20 text-xs text-red-200">
                    <UserX className="w-3 h-3" />
                    <UserLink userId={uid} name={uid.slice(0, 8)} className="text-red-200/80" />
                    <button
                      type="button"
                      data-testid={`convoy-unban-${uid}`}
                      onClick={async () => {
                        try {
                          const { data } = await api.post(`/convoys/${c.id}/unban/${uid}`);
                          setC(data);
                          toast.success(t('convoys.host.unbanned'));
                        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
                      }}
                      className="text-[10px] uppercase tracking-wider hover:text-white px-1.5 py-0.5 rounded bg-red-500/20 hover:bg-red-500/30 transition"
                    >
                      {t('convoys.host.unban')}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Pending join requests (host only) */}
        {isHost && pendingRequests.length > 0 && (
          <div className="mt-6 glass rounded-2xl p-5 border border-[#FFD479]/30" data-testid="convoy-pending-requests">
            <div className="text-xs uppercase tracking-[0.2em] text-[#FFD479] font-bold mb-3 inline-flex items-center gap-2">
              ⏳ {t('convoys.pending_requests') || 'Demandes en attente'} ({pendingRequests.length})
            </div>
            <div className="space-y-2">
              {pendingRequests.map((r) => (
                <div key={r.user_id} className="flex items-center justify-between gap-3 bg-white/5 rounded-xl px-3 py-2" data-testid={`pending-request-${r.user_id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    {r.avatar_url ? (
                      <img src={r.avatar_url} alt={r.name} className="w-7 h-7 rounded-full" />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-[#FFD479] text-black grid place-items-center text-[10px] font-bold">
                        {r.name?.[0]?.toUpperCase()}
                      </div>
                    )}
                    <UserLink userId={r.user_id} name={r.name} className="text-sm font-semibold truncate" />
                    <span className="text-[10px] text-white/40">{new Date(r.requested_at).toLocaleString()}</span>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <Button
                      data-testid={`accept-request-${r.user_id}`}
                      onClick={() => acceptRequest(r.user_id)}
                      size="sm"
                      className="rounded-full bg-emerald-500 hover:bg-emerald-500/90 text-white h-7 px-3 text-xs"
                    >
                      ✓ {t('convoys.accept') || 'Accepter'}
                    </Button>
                    <Button
                      data-testid={`reject-request-${r.user_id}`}
                      onClick={() => rejectRequest(r.user_id)}
                      size="sm"
                      variant="ghost"
                      className="rounded-full border border-red-500/30 text-red-300 hover:bg-red-500/10 h-7 px-3 text-xs"
                    >
                      ✕ {t('convoys.reject') || 'Refuser'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Chat */}
        <div className="mt-6">
          <div className="flex items-center gap-2 mb-3">
            <MessageCircle className="w-4 h-4 text-white/60" />
            <h3 className="font-display font-bold uppercase tracking-wider text-sm text-white/80">
              {t('convoys.discussion')} ({comments.length})
            </h3>
            {!isMember && !isAdmin && (
              <span className="text-[10px] uppercase tracking-widest text-white/40 ml-auto">
                {t('convoys.chat.members_only') || 'Réservé aux membres'}
              </span>
            )}
          </div>
          <ChatComposer
            onSubmit={submit}
            placeholder={
              user && user !== false && (isMember || isAdmin)
                ? (t('convoys.chat.placeholder') || 'Écris un message au convoi…')
                : (t('convoys.chat.placeholder_disabled') || 'Rejoins le convoi pour discuter')
            }
            disabled={!user || user === false || (!isMember && !isAdmin)}
            rows={2}
            sendVariant="cyan"
            testIdPrefix="convoy-chat"
            sendOnEnter
          />

          <div className="mt-4 space-y-2 max-h-[420px] overflow-y-auto pr-1" data-testid="convoy-chat-list">
            {comments.length === 0 && (
              <div className="text-white/40 text-sm py-6 text-center">{t('convoys.chat.empty')}</div>
            )}
            {comments.map((cm) => {
              const mine = user && user !== false && cm.author_id === user.id;
              return (
                <div
                  key={cm.id}
                  data-testid={`convoy-comment-${cm.id}`}
                  className={`max-w-[85%] rounded-2xl px-3 py-2 border ${
                    mine
                      ? 'ml-auto bg-[#00F0FF]/10 border-[#00F0FF]/30'
                      : 'bg-white/5 border-white/10'
                  }`}
                >
                  <div className="flex items-center gap-2 text-xs text-white/50">
                    <div className="w-5 h-5 rounded-full bg-[#00F0FF] text-black grid place-items-center text-[10px] font-bold">
                      {cm.author_name?.[0]?.toUpperCase()}
                    </div>
                    <UserLink userId={cm.author_id} name={cm.author_name} className="font-semibold text-white/80" />
                    {!mine && <AddFriendButton userId={cm.author_id} userName={cm.author_name} variant="icon" size="xs" />}
                    <span className="ml-auto text-[10px] text-white/40">{new Date(cm.created_at).toLocaleString()}</span>
                  </div>
                  {cm.text && <p className="text-sm text-white/90 mt-1.5 whitespace-pre-wrap break-words">{cm.text}</p>}
                  {cm.image_path && (
                    <button type="button" onClick={() => setLightbox(cm.image_path)} className="block mt-2" data-testid={`convoy-comment-image-${cm.id}`}>
                      <img src={fileUrl(cm.image_path)} alt="" className="max-h-60 rounded-lg border border-white/10 object-cover hover:opacity-90 transition" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <ImageLightbox src={lightbox} onClose={() => setLightbox(null)} />

      <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
        <AlertDialogContent className="bg-[#0A0A0A] border-white/10 text-white" data-testid="convoy-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-tight text-white">
              {t('convoys.confirm_delete') || 'Supprimer ce convoi ?'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              {c?.name}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="convoy-delete-cancel"
              className="rounded-full bg-white/5 hover:bg-white/10 text-white border-white/10"
            >
              {t('common.cancel') || 'Annuler'}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="convoy-delete-confirm"
              disabled={deleting}
              onClick={(e) => { e.preventDefault(); del(); }}
              className="rounded-full bg-red-500 hover:bg-red-500/90 text-white"
            >
              {deleting ? '…' : (t('common.delete') || 'Supprimer')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
